import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { getDeviceToken } from '../utils/deviceAuth';

function DeviceVerificationRequired({ message, onVerified, username, password }) {
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  // Poll for device verification status
  useEffect(() => {
    const checkInterval = setInterval(async () => {
      try {
        // Check device status without authentication
        const deviceToken = getDeviceToken();
        if (!deviceToken) {
          console.error('No device token found');
          return;
        }

        // Try to login again to check if device is now verified
        const response = await authAPI.login(username, password);
        
        if (!response.device.requires_verification && response.token) {
          // Device is now verified and we have a token
          clearInterval(checkInterval);
          if (onVerified) {
            onVerified();
          }
        }
      } catch (err) {
        // Silently fail - device might not be verified yet
        console.error('Error checking device status:', err);
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(checkInterval);
  }, [onVerified, username, password]);

  const handleResendVerification = async () => {
    setResending(true);
    setResendMessage('');

    try {
      await authAPI.resendVerification();
      setResendMessage('Verification email sent! Please check your inbox.');
    } catch (err) {
      setResendMessage(
        err.response?.data?.message || 'Failed to resend verification email'
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="verification-container">
      <div className="verification-card">
        <div className="verification-icon">
          <svg 
            width="64" 
            height="64" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
          </svg>
        </div>

        <h2>Device Verification Required</h2>
        
        <div className="verification-message">
          <p>{message}</p>
        </div>

        <div className="verification-instructions">
          <h3>What to do next:</h3>
          <ol>
            <li>Check your email inbox</li>
            <li>Click the verification link in the email</li>
            <li>Return to this page - it will automatically update</li>
          </ol>
        </div>

        {checking && (
          <div className="checking-status">
            <div className="spinner"></div>
            <p>Checking verification status...</p>
          </div>
        )}

        <div className="verification-actions">
          <button
            onClick={handleResendVerification}
            disabled={resending}
            className="btn-secondary"
          >
            {resending ? 'Sending...' : 'Resend Verification Email'}
          </button>
        </div>

        {resendMessage && (
          <div className={`message ${resendMessage.includes('sent') ? 'success' : 'error'}`}>
            {resendMessage}
          </div>
        )}

        <div className="verification-footer">
          <p className="help-text">
            This page will automatically refresh when your device is verified.
          </p>
        </div>
      </div>
    </div>
  );
}

export default DeviceVerificationRequired;
